import { AssociateUserRole, AssociatedCaseWorkerModel, CaseModel } from '../../../../api-service/clients';
import { DatePickerValue } from '../../../date-picker';
import { toStartOfDay } from '../../../../utilities';
import React, { useEffect, useState } from 'react';
import { moduleClientFactory } from '../../../../api-service/module-client-factory';
import { AssignAssociatedcaseworkerCasesComponent } from './assign-associatedcaseworker-cases-component';
import { isLoading } from '../../../wizard';
import { useNotifier } from '../../../notifier';

const client = moduleClientFactory.createCitizenCaseClient();
interface IAssignAssociatedcaseworkerCasesProps {
  caseIds: string[];
  citizenIds: string[];
  onCompleted?: () => void;
}
interface IMinMaxDates {
  minStartDate?: Date;
  maxEndDate?: Date;
}
export function AssignAssociatedcaseworkerCases(props: IAssignAssociatedcaseworkerCasesProps) {
  const { caseIds, citizenIds, onCompleted } = props;
  const [associateUserRoles, setAssociateUserRoles] = useState<AssociateUserRole[]>([]);
  const [errors, setErrors] = useState<AssociatedCaseWorkerModel>({} as AssociatedCaseWorkerModel);
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [minMaxResult, setMinMaxResult] = useState<{ minStartDate?: Date; maxEndDate?: Date }>();
  const [caseStartEndChecked, setCaseStartEndChecked] = useState(true);
  const [associatedCaseWorker, setAssociatedCaseWorker] = useState<AssociatedCaseWorkerModel>({
    startDate: new Date(),
    isResponsibleCaseworker: false,
    isResponsibleExternalOperator: false,
  } as AssociatedCaseWorkerModel);
  const { caseWorkerId, startDate, endDate, description } = associatedCaseWorker;

  const { dispatchMessageBox } = useNotifier();

  useEffect(() => {
    async function init() {
      const associateUserRolesResult = await client.getAssociateUserRoles();
      if (associateUserRolesResult.associateUserRoles) {
        setAssociateUserRoles(associateUserRolesResult.associateUserRoles.filter(x => x.isActive));
      }
      const minMaxResult = await client.getMinAndMAxDateFromCases({ caseIds });
      setMinMaxResult(minMaxResult as IMinMaxDates);
    }
    init();
  }, []);

  function handleValidate() {
    let isValid = true;

    const caseStartDate = toStartOfDay(minMaxResult?.minStartDate);
    const caseEndDate = toStartOfDay(minMaxResult?.maxEndDate);
    const userStartDate = toStartOfDay(startDate);
    const userEndDate = toStartOfDay(endDate);

    if (!caseWorkerId) {
      isValid = false;
      setErrors({ ...errors, caseWorkerId: 'Sagsbehandler skal udfyldes' });
    }
    if (caseStartDate && userStartDate && !caseStartEndChecked) {
      if (userStartDate < caseStartDate) {
        setErrors({ ...errors, startDate: 'Startdato skal være på eller efter de valgte sagers seneste startdato' });
        isValid = false;
      }
    }
    if (caseEndDate && userEndDate && !caseStartEndChecked) {
      if (userEndDate > caseEndDate) {
        setErrors({ ...errors, endDate: 'Slutdato skal være på eller før de valgte sagers tidligste slutdato' });
        isValid = false;
      }
    }
    if (caseEndDate && userStartDate && !caseStartEndChecked) {
      if (userStartDate > caseEndDate) {
        setErrors({ ...errors, startDate: 'Startdato skal være før eller på sagens slutdato' });
        isValid = false;
      }
    }
    if (userStartDate && userEndDate && !caseStartEndChecked) {
      if (userStartDate > userEndDate) {
        setErrors({ ...errors, startDate: 'Startdato skal være før eller på slutdato' });
        isValid = false;
      }
    }
    if (description && description.length > 200) {
      isValid = false;
    }

    return isValid;
  }
  function handleAssociateUserRoleChange(value: AssociateUserRole) {
    setAssociatedCaseWorker({ ...associatedCaseWorker, role: value?.id });
  }

  function handleStartDateChange(value: DatePickerValue) {
    setAssociatedCaseWorker({ ...associatedCaseWorker, startDate: value });
    setErrors({ ...errors, startDate: '' });
  }

  function handleEndDateChange(value: DatePickerValue) {
    setAssociatedCaseWorker({ ...associatedCaseWorker, endDate: value });
    setErrors({ ...errors, endDate: '' });
  }

  function handleDescriptionChange(event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) {
    setAssociatedCaseWorker({ ...associatedCaseWorker, description: event.target.value });
  }
  async function handleUserChange(value: string | undefined) {
    setAssociatedCaseWorker({ ...associatedCaseWorker, caseWorkerId: value || '' });
    setErrors({ ...errors, caseWorkerId: '' });
  }
  async function handleConfirm() {
    if (handleValidate()) {
      try {
        setIsLoading(true);
        const result = await client.createAssociatedCaseWorkerOnCases({
          associatedCaseWorkerModels: caseIds.map(x => ({ ...associatedCaseWorker, caseId: x })),
          citizenIds,
          caseStartEndChecked,
        });
        setIsOpen(false);
        onCompleted?.();
        if (result.inActiveCasesCount) {
          dispatchMessageBox('Supplerende sagsbehandler', `Supplerende sagsbehandler ikke tilføjet for ${result.inActiveCasesCount} afsluttede sager`);
        }
      } finally {
        setIsLoading(false);
      }
    }
  }
  return (
    <AssignAssociatedcaseworkerCasesComponent
      associateUserRoles={associateUserRoles}
      associatedCaseWorker={associatedCaseWorker}
      errors={errors}
      onAssociateUserRoleChange={handleAssociateUserRoleChange}
      onDescriptionChange={handleDescriptionChange}
      onEndDateChange={handleEndDateChange}
      onStartDateChange={handleStartDateChange}
      onUserChange={handleUserChange}
      isOpen={isOpen}
      isLoading={isLoading}
      onClose={() => setIsOpen(false)}
      onConfirm={handleConfirm}
      minStartDate={minMaxResult?.minStartDate}
      maxEndDate={minMaxResult?.maxEndDate}
      caseStartEndChecked={caseStartEndChecked}
      onCaseStartEndClicked={x => setCaseStartEndChecked(x)}
    />
  );
}
